import { ObjectiveFormData } from './types';
import * as yup from 'yup';
import { GoalId, GoalType } from '../types';
import { useCallback } from 'react';
import { useUpdateObjective } from './useObjectiveMutations';
import { useMutationErrorHandler } from '../../../../../../hooks/useMutationErrorHandler';
import i18next from 'i18next';
import { useGoalFormState, GoalFormApi } from './goalFormState';
import { GoalStatusWithRate } from './types';
import { ObjectiveMode } from '../../../../__generated__/graphql';

const i18prefix = 'performanceReview.goals2';

export const validationSchema = yup.object().shape({
  employeeId: yup
    .string()
    .nullable(true)
    .when('type', (type: GoalType, schema: yup.StringSchema) => {
      if (type === 'employee') {
        return schema.required();
      }
      return schema;
    }),
  teamId: yup
    .string()
    .nullable(true)
    .when('type', (type: GoalType, schema: yup.StringSchema) => {
      if (type === 'team') {
        return schema.required();
      }
      return schema;
    }),
  timePeriodId: yup.string().required(),
  name: yup.string().max(80).required(),
  dueDate: yup
    .date()
    .nullable(true)
    .min(
      yup.ref('startDate'),
      i18next.t(`${i18prefix}.validationEndDateShouldBeGreater`),
    ),
  statusWithRate: yup
    .object()
    .nullable(true)
    .test(
      'cancelReason',
      i18next.t(`${i18prefix}.validationCancelReasonEmpty`),
      function (value: GoalStatusWithRate | undefined) {
        if (!value) {
          return true;
        }
        if (
          value.status === 'CANCELED' &&
          (!value.cancelReason || !value.cancelReason.length)
        ) {
          return false;
        }
        return true;
      },
    ),
});

const getDefaultValues = (
  employeeId: string,
  objectiveMode: ObjectiveMode,
): ObjectiveFormData => ({
  type: 'employee',
  name: '',
  employeeId,
  teamId: undefined,
  description: '',
  visibility: 'public',
  timePeriodId: '',
  startDate: null,
  dueDate: null,
  // MBO uses phases + achievement scales for lifecycle/outcome, so we skip
  // DRAFT and create objectives directly as ON_TRACK.
  statusWithRate: {
    status: objectiveMode === ObjectiveMode.Mbo ? 'ON_TRACK' : 'DRAFT',
    rate: 'OPEN',
  },
  tagIds: [],
  categoryId: null,
  achievementScaleId: null,
  sharedWithEmployeeIds: [],
  keyResults: [],
  parentObjectiveId: null,
  manualProgress: false,
  goalOnTrackAutoState: true,
  weight: null,
  objectiveResult: null,
  alignedTo: [],
});

export const useCreateObjectiveForm = (
  meId: string,
  objectiveMode: ObjectiveMode,
): GoalFormApi<ObjectiveFormData> => {
  return useGoalFormState<ObjectiveFormData>(
    getDefaultValues(meId, objectiveMode),
    validationSchema,
  );
};

export const useUpdateObjectiveForm = (
  values: ObjectiveFormData,
): GoalFormApi<ObjectiveFormData> => {
  return useGoalFormState<ObjectiveFormData>(values, validationSchema);
};

export const useSubmitUpdate = (meId: string, goalId: GoalId) => {
  const errorHandler = useMutationErrorHandler();
  const [updateObjective] = useUpdateObjective(meId, goalId);

  const submitField = useCallback(
    async (fieldName: keyof ObjectiveFormData, value: unknown) => {
      await updateObjective({ [fieldName]: value }).catch((e) => {
        errorHandler(e);
        throw e;
      });
    },
    [errorHandler, updateObjective],
  );

  return { submitField };
};
